(() => {
  'use strict';

  const formatterCache = new Map();

  const countdownEl = document.querySelector('.js-password-countdown');
  if (!countdownEl) return;

  const targetInput = (countdownEl.dataset.countdownTarget || '').trim();
  const fallbackMessage = (countdownEl.dataset.countdownFallbackMessage || '').trim();
  const endText = (countdownEl.dataset.countdownEndText || 'LIVE NOW').trim() || 'LIVE NOW';
  const timeZone = resolveTimeZone((countdownEl.dataset.countdownTimezone || '').trim());

  const explicitTarget = parseTargetDate(targetInput, timeZone);
  const fallbackTarget = parseWeeklyFallback(fallbackMessage, timeZone);
  const defaultWeeklyTarget = getNextWeeklyTarget(5, 12, 0, 0, timeZone);
  const targetDate = explicitTarget || fallbackTarget || defaultWeeklyTarget;

  if (!targetDate || Number.isNaN(targetDate.getTime())) {
    if (fallbackMessage) {
      countdownEl.textContent = fallbackMessage.replace(/<[^>]*>/g, '').trim();
    } else {
      countdownEl.textContent = endText;
    }
    return;
  }

  let timerId = null;

  const tick = () => {
    const now = new Date();
    if (now.getTime() >= targetDate.getTime()) {
      countdownEl.textContent = endText;
      countdownEl.setAttribute('aria-label', endText);
      countdownEl.dataset.countdownState = 'ended';
      return;
    }

    const remaining = getRemainingParts(now, targetDate, timeZone);
    const display = formatCountdown(remaining);
    countdownEl.textContent = display;
    countdownEl.setAttribute(
      'aria-label',
      `${remaining.months} months ${remaining.days} days ${remaining.hours} hours ${remaining.minutes} minutes ${remaining.seconds} seconds`
    );
    countdownEl.dataset.countdownState = 'running';

    const nextDelayMs = 1000 - (Date.now() % 1000);
    timerId = window.setTimeout(tick, nextDelayMs);
  };

  tick();

  window.addEventListener(
    'pagehide',
    () => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    },
    { once: true }
  );

  function formatCountdown(parts) {
    return [parts.months, parts.days, parts.hours, parts.minutes, parts.seconds].map(pad2).join(':');
  }

  function pad2(value) {
    return String(Math.max(0, value)).padStart(2, '0');
  }

  function resolveTimeZone(inputZone) {
    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    if (!inputZone) return browserZone;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: inputZone }).format(new Date());
      return inputZone;
    } catch (error) {
      return browserZone;
    }
  }

  function parseTargetDate(rawInput, zone) {
    if (!rawInput) return null;
    const clean = rawInput.trim();

    if (/^-?\d{10,13}$/.test(clean)) {
      const value = clean.length === 13 ? Number(clean) : Number(clean) * 1000;
      const tsDate = new Date(value);
      return Number.isNaN(tsDate.getTime()) ? null : tsDate;
    }

    const normalized = clean.replace(/\s+/, 'T');
    if (hasExplicitOffset(normalized)) {
      const directDate = new Date(normalized);
      return Number.isNaN(directDate.getTime()) ? null : directDate;
    }

    const localMatch = clean.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?)?$/i
    );

    if (localMatch) {
      let hour = Number(localMatch[4] || 0);
      const minute = Number(localMatch[5] || 0);
      const second = Number(localMatch[6] || 0);
      const meridiem = (localMatch[7] || '').toLowerCase();

      if (meridiem === 'pm' && hour < 12) hour += 12;
      if (meridiem === 'am' && hour === 12) hour = 0;

      const localParts = {
        year: Number(localMatch[1]),
        month: Number(localMatch[2]),
        day: Number(localMatch[3]),
        hour,
        minute,
        second,
      };

      return zonedDateTimeToUtc(localParts, zone);
    }

    const fallbackDate = new Date(clean);
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }

  function hasExplicitOffset(value) {
    return /(?:z|[+\-]\d{2}:?\d{2})$/i.test(value);
  }

  function parseWeeklyFallback(rawMessage, zone) {
    if (!rawMessage) return null;

    const cleanMessage = rawMessage.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const match = cleanMessage.match(
      /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\s+(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?$/i
    );

    if (!match) return null;

    const targetWeekday = weekdayNameToIndex(match[1]);
    if (targetWeekday === null) return null;

    let hour = Number(match[2]);
    const minute = Number(match[3] || 0);
    const second = Number(match[4] || 0);
    const meridiem = (match[5] || '').toLowerCase();

    if (meridiem === 'pm' && hour < 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;

    return getNextWeeklyTarget(targetWeekday, hour, minute, second, zone);
  }

  function getNextWeeklyTarget(targetWeekday, hour, minute, second, zone) {
    const now = new Date();
    const nowZoned = getZonedParts(now, zone);
    const nowWeekday = nowZoned.weekday;
    const nowTotalSeconds = nowZoned.hour * 3600 + nowZoned.minute * 60 + nowZoned.second;
    const targetTotalSeconds = hour * 3600 + minute * 60 + second;

    let dayOffset = targetWeekday - nowWeekday;
    if (dayOffset < 0) dayOffset += 7;
    if (dayOffset === 0 && targetTotalSeconds <= nowTotalSeconds) dayOffset = 7;

    return zonedDateTimeToUtc(
      {
        year: nowZoned.year,
        month: nowZoned.month,
        day: nowZoned.day + dayOffset,
        hour,
        minute,
        second,
      },
      zone
    );
  }

  function weekdayNameToIndex(name) {
    const map = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    const normalized = String(name || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(map, normalized) ? map[normalized] : null;
  }

  function getRemainingParts(now, target, zone) {
    let cursor = new Date(now.getTime());

    const nowParts = getZonedParts(now, zone);
    const targetParts = getZonedParts(target, zone);

    let months = Math.max(0, (targetParts.year - nowParts.year) * 12 + (targetParts.month - nowParts.month));
    let monthCandidate = addMonthsInZone(cursor, months, zone);

    while (months > 0 && monthCandidate.getTime() > target.getTime()) {
      months -= 1;
      monthCandidate = addMonthsInZone(cursor, months, zone);
    }

    while (addMonthsInZone(cursor, months + 1, zone).getTime() <= target.getTime()) {
      months += 1;
    }

    cursor = addMonthsInZone(cursor, months, zone);

    const dayMs = 24 * 60 * 60 * 1000;
    let days = Math.max(0, Math.floor((target.getTime() - cursor.getTime()) / dayMs));
    let dayCandidate = addDaysInZone(cursor, days, zone);

    while (days > 0 && dayCandidate.getTime() > target.getTime()) {
      days -= 1;
      dayCandidate = addDaysInZone(cursor, days, zone);
    }

    while (addDaysInZone(cursor, days + 1, zone).getTime() <= target.getTime()) {
      days += 1;
    }

    cursor = addDaysInZone(cursor, days, zone);

    let remainderMs = Math.max(0, target.getTime() - cursor.getTime());
    const hourMs = 60 * 60 * 1000;
    const minuteMs = 60 * 1000;

    const hours = Math.floor(remainderMs / hourMs);
    remainderMs -= hours * hourMs;

    const minutes = Math.floor(remainderMs / minuteMs);
    remainderMs -= minutes * minuteMs;

    const seconds = Math.floor(remainderMs / 1000);

    return {
      months,
      days,
      hours,
      minutes,
      seconds,
    };
  }

  function addMonthsInZone(date, months, zone) {
    if (months === 0) return new Date(date.getTime());

    const zoned = getZonedParts(date, zone);
    const monthIndex = zoned.month - 1 + months;
    const year = zoned.year + Math.floor(monthIndex / 12);
    const normalizedMonthIndex = ((monthIndex % 12) + 12) % 12;
    const month = normalizedMonthIndex + 1;
    const day = Math.min(zoned.day, daysInMonth(year, month));

    return zonedDateTimeToUtc(
      {
        year,
        month,
        day,
        hour: zoned.hour,
        minute: zoned.minute,
        second: zoned.second,
      },
      zone
    );
  }

  function addDaysInZone(date, days, zone) {
    if (days === 0) return new Date(date.getTime());

    const zoned = getZonedParts(date, zone);
    return zonedDateTimeToUtc(
      {
        year: zoned.year,
        month: zoned.month,
        day: zoned.day + days,
        hour: zoned.hour,
        minute: zoned.minute,
        second: zoned.second,
      },
      zone
    );
  }

  function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
  }

  function getZonedParts(date, zone) {
    let formatter = formatterCache.get(zone);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        weekday: 'long',
        hourCycle: 'h23',
      });
      formatterCache.set(zone, formatter);
    }

    const parts = formatter.formatToParts(date);
    const mapped = {};
    for (const part of parts) {
      if (part.type !== 'literal') mapped[part.type] = part.value;
    }

    return {
      year: Number(mapped.year),
      month: Number(mapped.month),
      day: Number(mapped.day),
      hour: Number(mapped.hour),
      minute: Number(mapped.minute),
      second: Number(mapped.second),
      weekday: weekdayNameToIndex(mapped.weekday),
    };
  }

  function getTimeZoneOffsetMs(date, zone) {
    const parts = getZonedParts(date, zone);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return asUtc - date.getTime();
  }

  function zonedDateTimeToUtc(parts, zone) {
    const guess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    let utcTime = guess;

    for (let i = 0; i < 4; i += 1) {
      const offset = getTimeZoneOffsetMs(new Date(utcTime), zone);
      const nextUtcTime = guess - offset;
      if (Math.abs(nextUtcTime - utcTime) < 1) {
        utcTime = nextUtcTime;
        break;
      }
      utcTime = nextUtcTime;
    }

    return new Date(utcTime);
  }
})();
